import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Download, Search, X, Receipt, Filter, FileText, AlertCircle, TrendingUp, TrendingDown, ChevronRight, ChevronDown, Settings, Send, Copy } from "lucide-react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, LabelList,
} from "recharts";
import { api, API, getToken, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TemplateInput } from "@/components/tahsilat/TemplateInput";
import { MahkemeOzetCard } from "@/components/gider_avans/MahkemeOzetCard";
import SavedFilterPresets from "./SavedFilterPresets";
import QuickDateFilters, { computeQuickRange } from "./QuickDateFilters";
import AvansTuruTemplateManager from "./gider_avans/AvansTuruTemplateManager";
import { DateInput } from "@/components/DateInput";
import { SortTh, useColumnSort } from "@/components/SortTh";
import { LoadError, LoadingRow } from "@/components/LoadingState";

const YATIRILDIGI_YER_OPTIONS = ["Mahkeme", "İcra"];

// Column config. `avans_turu` options are injected at render time from templates.
const COLUMNS = [
  { k: "muvekkil",                      l: "Müvekkil",                      t: "text" },
  { k: "base_no",                       l: "Base No",                       t: "text" },
  { k: "borclu",                        l: "Borçlu",                        t: "text" },
  { k: "mahkeme_esas_no",               l: "Mahkeme Esas No",               t: "text" },
  { k: "icra_esas_no",                  l: "İcra Esas No",                  t: "text" },
  { k: "masraf_yatirma_tarihi",         l: "Masraf Yatırma Tarihi",         t: "date" },
  { k: "yatirilan_avans",               l: "Yatırılan Avans (₺)",           t: "money" },
  { k: "avans_turu",                    l: "Avans Türü",                    t: "avans_turu_tpl" },
  { k: "yatirildigi_yer",               l: "Yatırıldığı Yer",               t: "select", options: YATIRILDIGI_YER_OPTIONS },
  { k: "mahkeme_icra_dairesi",          l: "Mahkeme / İcra Dairesi",        t: "tpl" },
  { k: "iade_tarihi",                   l: "İade Tarihi",                   t: "date" },
  { k: "iade_gelen_hesap",              l: "İade Gelen Hesap",              t: "tpl" },
  { k: "iade_gelen_tutar",              l: "İade Gelen Tutar (₺)",          t: "money" },
  { k: "fark",                          l: "Fark (₺)",                      t: "money", computed: true },
  { k: "muvekkile_gonderilme_tarihi",   l: "Müvekkile Gönderilme Tarihi",   t: "date" },
  { k: "notlar",                        l: "Notlar",                        t: "textarea" },
];
const EDITABLE_COLUMNS = COLUMNS.filter((c) => !c.computed);
const GIDER_NUM_KEYS = COLUMNS.filter((c) => c.t === "money").map((c) => c.k);
const TABLE_COLUMNS = [COLUMNS.find((c) => c.k === "muvekkil"), COLUMNS.find((c) => c.k === "borclu"),
  ...COLUMNS.filter((c) => c.k !== "muvekkil" && c.k !== "borclu")];

const fmtMoney = (v) => (v === null || v === undefined || v === "")
  ? "—"
  : new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(Number(v) || 0);

// Small colored dot used wherever an Avans Türü value is shown
function AvansTuruDot({ color, size = 8, className = "" }) {
  if (!color) return null;
  return (
    <span
      className={`inline-block rounded-full border border-black/10 shrink-0 ${className}`}
      style={{ width: size, height: size, backgroundColor: color }}
      aria-hidden="true"
    />
  );
}

/**
 * Gider Avansları satırı için uyarı rengi:
 *   Öncelik — durum > tarih.
 *   - Müvekkile gönderildi (muvekkile_gonderilme_tarihi dolu) → sarı (iade var ama henüz gönderilmemiş)? Hayır → tamamlandı, renk yok.
 *   - İade gelmiş (iade_tarihi dolu) ama müvekkile gönderilmemiş:
 *       · iade_tarihi geçmiş  → kırmızı (gecikmiş, acil gönder)
 *       · iade_tarihi bugün   → turuncu (bugün gönderilmeli)
 *       · iade_tarihi gelecek → sarı   (bekliyor)
 *   - İade yok → renk yok.
 */
function giderRowStatusClass(r) {
  const sent = (r?.muvekkile_gonderilme_tarihi || "").trim();
  const iade = (r?.iade_tarihi || "").trim();
  if (sent) return "";
  if (!iade) return "";
  const todayISO = new Date().toISOString().slice(0, 10);
  if (iade < todayISO) {
    // Aging: deeper rose for older overdue rows
    const days = giderOverdueDays(r);
    if (days >= 15) return "bg-rose-300 hover:bg-rose-400";
    if (days >= 8)  return "bg-rose-200 hover:bg-rose-300";
    return "bg-rose-100 hover:bg-rose-200";
  }
  if (iade === todayISO) return "bg-orange-100 hover:bg-orange-200";
  return "bg-yellow-100 hover:bg-yellow-200";
}

/** Gecikme gün sayısı: sadece gönderilmemiş + iade_tarihi geçmiş satırlar için pozitif değer. */
function giderOverdueDays(r) {
  const sent = (r?.muvekkile_gonderilme_tarihi || "").trim();
  const iade = (r?.iade_tarihi || "").trim();
  if (sent || !iade) return 0;
  // Compute midnight-local diff
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(iade + "T00:00:00");
  if (Number.isNaN(d.getTime())) return 0;
  const diff = Math.floor((today - d) / 86400000);
  return diff > 0 ? diff : 0;
}

function emptyForm() {
  const f = {};
  EDITABLE_COLUMNS.forEach((c) => { f[c.k] = c.t === "money" ? 0 : ""; });
  return f;
}

const GIDER_COPY_KEYS = ["muvekkil", "base_no", "borclu", "mahkeme_esas_no", "icra_esas_no", "yatirildigi_yer", "mahkeme_icra_dairesi"];

const giderDraftHasContent = (f) => Object.values(f).some((v) => (typeof v === "number" ? v !== 0 : String(v ?? "").trim() !== ""));

export default function GiderAvansTab() {
  const { user } = useAuth();
  const canManageTemplates = user && ["admin", "yonetici", "muhasebeci", "junior_muhasebe"].includes(user.role);
  const [rows, setRows] = useState([]);
  const colSort = useColumnSort(rows, GIDER_NUM_KEYS);
  const [totals, setTotals] = useState({ yatirilan_avans: 0, iade_gelen_tutar: 0, fark: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ muvekkil: "", avans_turu: "", from_date: "", to_date: "", from_iade: "", to_iade: "" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [creditors, setCreditors] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Row selection + bulk edit
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showBulkFillYer, setShowBulkFillYer] = useState(false);
  // Avans Türü şablonlar (dinamik)
  const [avansTuruOptions, setAvansTuruOptions] = useState([]); // [{id, ad, is_active}]
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  // Trend
  const [trendYear, setTrendYear] = useState(new Date().getFullYear());
  const [trendData, setTrendData] = useState([]);
  const [trendTotals, setTrendTotals] = useState({ yatirilan: 0, iade: 0, fark: 0 });
  const [trendLoading, setTrendLoading] = useState(false);

  const activeFilterCount =
    (filters.muvekkil ? 1 : 0) + (filters.avans_turu ? 1 : 0) +
    (filters.from_date ? 1 : 0) + (filters.to_date ? 1 : 0) +
    (filters.from_iade ? 1 : 0) + (filters.to_iade ? 1 : 0);

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (search) p.set("q", search);
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    const qs = p.toString();
    return qs ? `?${qs}` : "";
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, cr] = await Promise.all([
        api.get(`/gider-avans${buildQuery()}`),
        api.get("/creditors"),
      ]);
      setRows(r.data?.rows || []);
      setTotals(r.data?.totals || { yatirilan_avans: 0, iade_gelen_tutar: 0, fark: 0 });
      setCreditors(cr.data?.rows || cr.data || []);
      setLoadError(false);
    } catch {
      setLoadError(true);
      toast.error("Veriler yüklenemedi");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const firstMahkeme = React.useRef(true);
  useEffect(() => {
    if (firstMahkeme.current) { firstMahkeme.current = false; return; }
    load(); /* eslint-disable-next-line */
  }, [filters.mahkeme]);

  const loadTrend = async (y = trendYear) => {
    setTrendLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("year", String(y));
      if (filters.muvekkil) p.set("muvekkil", filters.muvekkil);
      const r = await api.get(`/gider-avans/trend?${p.toString()}`);
      setTrendData(r.data?.data || []);
      setTrendTotals(r.data?.totals || { yatirilan: 0, iade: 0, fark: 0 });
    } catch {
      /* silent */
    } finally { setTrendLoading(false); }
  };
  useEffect(() => { loadTrend(trendYear); /* eslint-disable-next-line */ }, [trendYear, filters.muvekkil]);

  const previewFark = useMemo(() => {
    const a = Number(form.yatirilan_avans) || 0;
    const b = Number(form.iade_gelen_tutar) || 0;
    return Math.round((a - b) * 100) / 100;
  }, [form.yatirilan_avans, form.iade_gelen_tutar]);

  // Top-6 most used avans turu for one-tap filter chips (auto-ranked)
  const topAvansTuruChips = useMemo(() => {
    return (avansTuruOptions || [])
      .filter((t) => t.is_active)
      .slice()
      .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))
      .slice(0, 6);
  }, [avansTuruOptions]);

  // Lookup from avans_turu name (case-insensitive) → color for colored dots
  const avansTuruColorMap = useMemo(() => {
    const m = {};
    (avansTuruOptions || []).forEach((t) => {
      if (t.ad && t.color) m[t.ad.toLowerCase()] = t.color;
    });
    return m;
  }, [avansTuruOptions]);
  const colorForAvansTuru = (name) =>
    name ? (avansTuruColorMap[String(name).toLowerCase()] || null) : null;

  const emptyYerCount = useMemo(
    () => rows.filter((r) => !r.yatirildigi_yer || !String(r.yatirildigi_yer).trim()).length,
    [rows]
  );

  // Overdue rows (iade geçmiş, müvekkile gönderilmemiş, >= 15 gün)
  const overdue15 = useMemo(
    () => rows.filter((r) => giderOverdueDays(r) >= 15),
    [rows]
  );

  const selectOverdue15 = () => {
    setSelectedIds(new Set(overdue15.map((r) => r.id)));
    if (overdue15.length > 0) {
      toast.success(`${overdue15.length} satır seçildi (15+ gün gecikmiş)`);
    } else {
      toast.info("15+ gün gecikmiş kayıt yok");
    }
  };

  const bulkMarkSent = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const today = new Date().toISOString().slice(0, 10);
    if (!window.confirm(`${ids.length} satır bugünkü tarih ile 'Müvekkile Gönderildi' olarak işaretlenecek. Onaylıyor musunuz?`)) return;
    // Snapshot previous values for undo
    const prev = new Map();
    rows.forEach((r) => { if (ids.includes(r.id)) prev.set(r.id, r.muvekkile_gonderilme_tarihi || ""); });
    try {
      const r = await api.post("/gider-avans/bulk-edit", { ids, patch: { muvekkile_gonderilme_tarihi: today } });
      load();
      toast.success(`${r.data.updated} satır müvekkile gönderildi olarak işaretlendi`, {
        duration: 8000,
        action: {
          label: "Geri Al",
          onClick: async () => {
            // Group by previous value and call bulk-edit per group; empty-string reverts aren't supported by bulk-edit (empty skipped),
            // so fall back to per-row PUT for those with prior empty value.
            const haveValue = [], hadEmpty = [];
            prev.forEach((pval, id) => (pval ? haveValue : hadEmpty).push({ id, pval }));
            try {
              const calls = [];
              // Group haveValue by distinct values
              const byVal = new Map();
              haveValue.forEach((e) => { if (!byVal.has(e.pval)) byVal.set(e.pval, []); byVal.get(e.pval).push(e.id); });
              byVal.forEach((idList, val) => {
                calls.push(api.post("/gider-avans/bulk-edit", { ids: idList, patch: { muvekkile_gonderilme_tarihi: val } }));
              });
              // For rows that previously had empty, we need PUT with full payload (unset today's value). Use per-row PATCH-less fallback.
              for (const e of hadEmpty) {
                const row = rows.find((rr) => rr.id === e.id);
                if (!row) continue;
                const put = { ...row, muvekkile_gonderilme_tarihi: null };
                delete put.id; delete put.created_at; delete put.updated_at; delete put.created_by; delete put.updated_by;
                calls.push(api.put(`/gider-avans/${e.id}`, put));
              }
              await Promise.all(calls);
              toast.success(`${ids.length} satır eski durumuna döndürüldü`);
              load();
            } catch {
              toast.error("Geri alınamadı");
            }
          },
        },
      });
    } catch {
      toast.error("Toplu güncelleme başarısız");
    }
  };

  // Avans Türü artık dinamik şablonlardan geliyor; otomatik doldurma kuralı KALDIRILDI
  // (kullanıcı tercihine göre her zaman manuel seçim).

  const loadAvansTuruTemplates = async () => {
    try {
      const r = await api.get("/avans-turu-templates");
      setAvansTuruOptions(r.data?.rows || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadAvansTuruTemplates(); }, []);

  const draftKey = user ? `gider_avans_draft_${user.id || user.email}` : null;
  const [draftInfo, setDraftInfo] = useState(null);
  useEffect(() => {
    if (!modalOpen || editing || !draftKey) return;
    if (giderDraftHasContent(form)) localStorage.setItem(draftKey, JSON.stringify({ form, saved_at: new Date().toISOString() }));
    else localStorage.removeItem(draftKey);
  }, [form, modalOpen, editing, draftKey]);

  const openAdd = () => {
    setEditing(null);
    setCopySource(null);
    let d = null;
    try { d = draftKey ? JSON.parse(localStorage.getItem(draftKey) || "null") : null; } catch { d = null; }
    setForm({ ...emptyForm(), ...(d?.form || {}) });
    setDraftInfo(d?.saved_at || null);
    setModalOpen(true);
  };
  const [copySource, setCopySource] = useState(null);
  const openCopy = (r) => {
    if (draftKey && localStorage.getItem(draftKey)
      && !window.confirm("Kaydedilmemiş bir Yeni Gider Avansı taslağınız var. Kopyalama bu taslağın yerini alacak. Devam edilsin mi?")) return;
    setEditing(null);
    const f = emptyForm();
    GIDER_COPY_KEYS.forEach((k) => { if (r[k] != null) f[k] = r[k]; });
    setForm(f);
    setDraftInfo(null);
    setCopySource(r);
    setModalOpen(true);
  };
  const discardDraft = () => {
    if (draftKey) localStorage.removeItem(draftKey);
    setForm(emptyForm());
    setDraftInfo(null);
    toast.success("Taslak silindi");
  };
  const openEdit = (r) => {
    setEditing(r);
    setCopySource(null);
    const f = emptyForm();
    EDITABLE_COLUMNS.forEach((c) => { f[c.k] = r[c.k] ?? (c.t === "money" ? 0 : ""); });
    setForm(f);
    setModalOpen(true);
  };

  const handleSave = async () => {
    // Soft requirement: Yatırıldığı Yer boşsa onay sor
    if (!form.yatirildigi_yer || !String(form.yatirildigi_yer).trim()) {
      const ok = window.confirm(
        "⚠️ 'Yatırıldığı Yer' alanı boş. Mahkeme veya İcra seçmediniz.\n\nYine de boş olarak kaydetmek istiyor musunuz?"
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      ["masraf_yatirma_tarihi", "iade_tarihi", "muvekkile_gonderilme_tarihi"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.yatirilan_avans = Number(payload.yatirilan_avans) || 0;
      payload.iade_gelen_tutar = Number(payload.iade_gelen_tutar) || 0;
      if (editing) {
        await api.put(`/gider-avans/${editing.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post("/gider-avans", payload);
        if (draftKey) localStorage.removeItem(draftKey);
        setDraftInfo(null);
        toast.success("Eklendi");
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`"${r.base_no || r.borclu || r.id}" silinecek. Onaylıyor musunuz?`)) return;
    try {
      await api.delete(`/gider-avans/${r.id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  // --- Row selection ---
  const toggleRowSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const toggleAll = () => {
    if (selectedIds.size === rows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map((r) => r.id)));
  };
  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < rows.length;

  const handleExport = async (fmt) => {
    setDownloading(true);
    try {
      const p = new URLSearchParams(buildQuery().slice(1));
      p.set("format", fmt);
      const res = await fetch(`${API}/gider-avans/export?${p.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.setAttribute("download", `gider_avanslari.${fmt === "csv" ? "csv" : "xlsx"}`);
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("İndirildi");
    } catch { toast.error("Dışa aktarılamadı"); }
    finally { setDownloading(false); }
  };

  const handleExportMuvekkilPdf = async () => {
    setDownloading(true);
    try {
      const p = new URLSearchParams(buildQuery().slice(1));
      const res = await fetch(`${API}/gider-avans/pdf-by-muvekkil?${p.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "PDF üretilemedi");
      }
      const blob = await res.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.setAttribute("download", `gider_avanslari_muvekkil.pdf`);
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Müvekkil bazlı PDF indirildi");
    } catch (e) { toast.error(e.message || "PDF üretilemedi"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-4" data-testid="gider-avans-tab">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 font-bold text-slate-900 text-base">
              <Receipt size={18} className="text-emerald-700" /> Gider Avansları
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {rows.length} kayıt{selectedIds.size > 0 && <> · <strong className="text-emerald-700">{selectedIds.size} seçili</strong></>} · Yatırılan: <strong>{fmtMoney(totals.yatirilan_avans)}</strong> · İade: <strong>{fmtMoney(totals.iade_gelen_tutar)}</strong> · Fark: <strong>{fmtMoney(totals.fark)}</strong>
              {emptyYerCount > 0 && (
                <>
                  {" · "}
                  <button
                    type="button"
                    onClick={() => setShowBulkFillYer(true)}
                    className="inline-flex items-center gap-1 text-amber-800 bg-amber-100 border border-amber-300 hover:bg-amber-200 px-1.5 py-0.5 rounded font-semibold cursor-pointer"
                    title={`${emptyYerCount} kayıtta 'Yatırıldığı Yer' boş. Tek tıkla hepsini doldur.`}
                    data-testid="gider-empty-yer-badge"
                  >
                    <AlertCircle size={11}/> {emptyYerCount} Yatırıldığı Yer eksik · Toplu Doldur
                  </button>
                </>
              )}
              {overdue15.length > 0 && (
                <>
                  {" · "}
                  <button
                    type="button"
                    onClick={selectOverdue15}
                    className="inline-flex items-center gap-1 text-rose-900 bg-rose-200 border border-rose-500 hover:bg-rose-300 px-1.5 py-0.5 rounded font-bold cursor-pointer"
                    title={`${overdue15.length} satır 15+ gün gecikmiş. Tek tıkla seç.`}
                    data-testid="gider-select-overdue15-btn"
                  >
                    <AlertCircle size={11}/> {overdue15.length} satır 15+ gün gecikmiş · Hepsini Seç
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                placeholder="Ara (müvekkil, base no, borçlu...)"
                className="pl-7 pr-2 py-1.5 border border-slate-300 rounded-md text-sm w-56"
                data-testid="gider-search"
              />
            </div>
            <button
              onClick={() => setFiltersOpen((x) => !x)}
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md ${filtersOpen || activeFilterCount > 0 ? "bg-indigo-600 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"}`}
              data-testid="gider-filter-toggle"
            >
              <Filter size={12} /> Filtre
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-white text-indigo-700 text-[10px] font-bold">{activeFilterCount}</span>
              )}
            </button>
            {selectedIds.size > 0 && (
              <>
                <button
                  onClick={bulkMarkSent}
                  data-testid="gider-bulk-mark-sent-btn"
                  className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1.5 rounded-md text-xs font-semibold"
                  title="Seçili satırlara bugünkü tarih ile 'Müvekkile Gönderildi' işareti vur"
                >
                  <Send size={12}/> Müvekkile Gönderildi
                  <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-white text-emerald-700 text-[10px] font-bold">{selectedIds.size}</span>
                </button>
                <button
                  onClick={() => setShowBulkEdit(true)}
                  data-testid="gider-bulk-edit-btn"
                  className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1.5 rounded-md text-xs font-semibold"
                >
                  <Edit size={12}/> Toplu Düzenle
                  <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-white text-amber-700 text-[10px] font-bold">{selectedIds.size}</span>
                </button>
              </>
            )}
            <button
              onClick={handleExportMuvekkilPdf}
              disabled={downloading}
              className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white px-2.5 py-1.5 rounded-md text-xs font-semibold"
              data-testid="gider-export-muvekkil-pdf"
              title="Her müvekkil için ayrı sayfalı PDF (kapak özet + QR doğrulama)"
            ><FileText size={12}/> Müvekkil PDF</button>
            <button
              onClick={() => handleExport("xlsx")}
              disabled={downloading}
              className="inline-flex items-center gap-1 bg-emerald-700 hover:bg-emerald-800 text-white px-2.5 py-1.5 rounded-md text-xs font-semibold"
              data-testid="gider-export-xlsx"
            ><Download size={12}/> Excel</button>
            <button
              onClick={() => handleExport("csv")}
              disabled={downloading}
              className="inline-flex items-center gap-1 bg-sky-600 hover:bg-sky-700 text-white px-2.5 py-1.5 rounded-md text-xs font-semibold"
              data-testid="gider-export-csv"
            ><Download size={12}/> CSV</button>
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white px-2.5 py-1.5 rounded-md text-xs font-semibold"
              data-testid="gider-add-btn"
            ><Plus size={13}/> Yeni Kayıt</button>
          </div>
        </div>

        {/* Filter panel */}
        {filtersOpen && (
          <div className="mb-3 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3" data-testid="gider-filters-panel">
            {/* Saved presets bar */}
            <SavedFilterPresets
              scope="gider_avans"
              currentFilters={filters}
              currentColFilters={{}}
              onApply={(f) => {
                setFilters({
                  muvekkil: f.muvekkil || "",
                  avans_turu: f.avans_turu || "",
                  from_date: f.from_date || "",
                  to_date: f.to_date || "",
                  from_iade: f.from_iade || "",
                  to_iade: f.to_iade || "",
                });
                setTimeout(load, 50);
              }}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Müvekkil</label>
                <select value={filters.muvekkil} onChange={(e) => setFilters({ ...filters, muvekkil: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm">
                  <option value="">Hepsi</option>
                  {creditors.map((c) => <option key={c.id} value={c.ad}>{c.ad}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Avans Türü</label>
                <select value={filters.avans_turu} onChange={(e) => setFilters({ ...filters, avans_turu: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm">
                  <option value="">Hepsi</option>
                  {avansTuruOptions.map((t) => <option key={t.id} value={t.ad}>{t.ad}</option>)}
                </select>
              </div>
              <div className="md:col-span-1"></div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Masraf Tarihi — Başl.</label>
                <DateInput value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="gider-filter-from-date" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">Masraf Tarihi — Bitiş</label>
                <DateInput value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="gider-filter-to-date" />
              </div>
              <div className="md:col-span-1">
                <QuickDateFilters
                  label="Masraf"
                  testPrefix="gider-quick-masraf"
                  onApply={({ from, to }) => {
                    setFilters((f) => ({ ...f, from_date: from, to_date: to }));
                    setTimeout(load, 60);
                  }}
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">İade Tarihi — Başl.</label>
                <DateInput value={filters.from_iade} onChange={(e) => setFilters({ ...filters, from_iade: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="gider-filter-from-iade" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block mb-0.5">İade Tarihi — Bitiş</label>
                <DateInput value={filters.to_iade} onChange={(e) => setFilters({ ...filters, to_iade: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-sm" data-testid="gider-filter-to-iade" />
              </div>
              <div className="md:col-span-1">
                <QuickDateFilters
                  label="İade"
                  testPrefix="gider-quick-iade"
                  onApply={({ from, to }) => {
                    setFilters((f) => ({ ...f, from_iade: from, to_iade: to }));
                    setTimeout(load, 60);
                  }}
                />
              </div>
            </div>
            <div className="flex items-end gap-2">
              <button onClick={load} className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-md" data-testid="gider-filter-apply">Uygula</button>
              <button onClick={() => { setFilters({ muvekkil: "", avans_turu: "", from_date: "", to_date: "", from_iade: "", to_iade: "" }); setSearch(""); setTimeout(load, 50); }} className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-xs font-semibold rounded-md">Temizle</button>
            </div>
          </div>
        )}

        {/* Quick Avans Türü chip bar (auto-ranked by usage) */}
        {topAvansTuruChips.length > 0 && (
          <div className="mb-3 flex items-center gap-1.5 flex-wrap" data-testid="gider-avans-turu-chips">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">Hızlı Filtre:</span>
            <button
              onClick={() => { setFilters((f) => ({ ...f, avans_turu: "" })); setTimeout(load, 50); }}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition ${!filters.avans_turu ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
              data-testid="gider-avans-turu-chip-all"
            >Hepsi</button>
            {topAvansTuruChips.map((t) => {
              const active = filters.avans_turu === t.ad;
              return (
                <button
                  key={t.id}
                  onClick={() => { setFilters((f) => ({ ...f, avans_turu: active ? "" : t.ad })); setTimeout(load, 50); }}
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition inline-flex items-center gap-1.5 ${active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-300 hover:bg-indigo-50 hover:border-indigo-300"}`}
                  data-testid={`gider-avans-turu-chip-${t.id}`}
                  title={`${t.usage_count || 0} kayıt`}
                >
                  <AvansTuruDot color={t.color} size={8} />
                  {t.ad}
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[9px] font-bold ${active ? "bg-white text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{t.usage_count || 0}</span>
                </button>
              );
            })}
          </div>
        )}

        <MahkemeOzetCard rows={rows} active={filters.mahkeme || ""} pdfQuery={buildQuery()}
          onSelect={(name) => setFilters((f) => ({ ...f, mahkeme: f.mahkeme === name ? "" : name }))} />

        {/* Trend chart */}
        <GiderAvansTrendCard
          year={trendYear}
          onYearChange={setTrendYear}
          data={trendData}
          totals={trendTotals}
          loading={trendLoading}
        />

        {/* Table */}
        <div className="flex items-center gap-2 flex-wrap text-[10px] mb-1.5" data-testid="gider-color-legend">
          <span className="font-bold uppercase tracking-wider text-slate-500">Renk Kodu:</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300"></span><span className="text-slate-600">İade bekliyor</span></span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-100 border border-orange-300"></span><span className="text-slate-600">İade bugün — gönderilmedi</span></span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-100 border border-rose-300"></span><span className="text-slate-600">1-7 gün gecikmiş</span></span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-200 border border-rose-400"></span><span className="text-slate-600">8-14 gün gecikmiş</span></span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-300 border border-rose-500"></span><span className="text-slate-600">15+ gün gecikmiş</span></span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-slate-300"></span><span className="text-slate-600">Tamamlandı / iade yok</span></span>
        </div>
        <div className="sticky-table border border-slate-200 rounded-lg">
          <table className="gider-table min-w-full text-xs">
            <thead className="bg-slate-100 text-slate-700 text-left">
              <tr>
                <th className="px-2 py-2 text-center w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    data-testid="gider-select-all"
                  />
                </th>
                {TABLE_COLUMNS.map((c) => (
                  <SortTh key={c.k} k={c.k} label={c.l} sort={colSort.sort} onSort={colSort.toggle}
                    className="px-2 py-2 font-bold whitespace-nowrap" testid={`gider-sort-${c.k}`} />
                ))}
                <th className="px-2 py-2 font-bold text-right whitespace-nowrap">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading && <LoadingRow colSpan={COLUMNS.length + 2} text="Gider avansları yükleniyor..." />}
              {!loading && loadError && (
                <tr><td colSpan={COLUMNS.length + 2}><LoadError text="Gider avansları yüklenemedi." onRetry={load} /></td></tr>
              )}
              {!loading && !loadError && rows.length === 0 && (
                <tr><td colSpan={COLUMNS.length + 2} className="px-3 py-6 text-center text-slate-400 text-sm italic">Kayıt yok</td></tr>
              )}
              {!loading && colSort.sorted.map((r) => {
                const isSel = selectedIds.has(r.id);
                const statusCls = giderRowStatusClass(r);
                return (
                  <tr key={r.id} className={`border-t border-slate-100 ${statusCls || "hover:bg-slate-50"} ${!statusCls && isSel ? "bg-emerald-50" : ""}`} data-testid={`gider-row-${r.id}`}>
                    <td className="px-2 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleRowSelect(r.id)}
                        data-testid={`gider-select-${r.id}`}
                      />
                    </td>
                    {TABLE_COLUMNS.map((c) => {
                      const empty = (r[c.k] === "" || r[c.k] === null || r[c.k] === undefined);
                      if (c.k === "muvekkil") {
                        const overdueDays = giderOverdueDays(r);
                        return (
                          <td key={c.k} className="px-2 py-1.5 whitespace-nowrap" data-testid={`gider-cell-muvekkil-${r.id}`}>
                            <span className="inline-flex items-center gap-1.5">
                              <span>{r[c.k] || ""}</span>
                              {overdueDays > 0 && (
                                <span
                                  data-testid={`gider-overdue-badge-${r.id}`}
                                  title={`İade tarihi ${fmtDate(r.iade_tarihi)} — müvekkile henüz gönderilmedi`}
                                  className={`inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${
                                    overdueDays >= 15 ? "bg-rose-700 text-white border-rose-800 animate-pulse" :
                                    overdueDays >= 8  ? "bg-rose-600 text-white border-rose-700" :
                                    overdueDays >= 4  ? "bg-rose-500 text-white border-rose-600" :
                                                        "bg-rose-100 text-rose-800 border-rose-300"
                                  }`}
                                >
                                  <AlertCircle size={9} /> {overdueDays} gün gecikmiş
                                </span>
                              )}
                            </span>
                          </td>
                        );
                      }
                      if (c.k === "yatirildigi_yer" && empty) {
                        return (
                          <td
                            key={c.k}
                            className="px-2 py-1.5 whitespace-nowrap"
                            data-testid={`gider-cell-yatirildigi-yer-empty-${r.id}`}
                            title="Yatırıldığı Yer boş · tıklayarak doldurun"
                          >
                            <button
                              onClick={() => openEdit(r)}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 px-1.5 py-0.5 rounded"
                            >
                              <AlertCircle size={11}/> Eksik
                            </button>
                          </td>
                        );
                      }
                      if (c.k === "avans_turu" && !empty) {
                        return (
                          <td
                            key={c.k}
                            className="px-2 py-1.5 whitespace-nowrap"
                            data-testid={`gider-cell-avans-turu-${r.id}`}
                          >
                            <span className="inline-flex items-center gap-1.5">
                              <AvansTuruDot color={colorForAvansTuru(r[c.k])} size={9} />
                              {r[c.k]}
                            </span>
                          </td>
                        );
                      }
                      return (
                        <td key={c.k} className={`px-2 py-1.5 whitespace-nowrap ${c.t === "money" ? "text-right font-semibold" : ""}`}>
                          {c.t === "money" ? fmtMoney(r[c.k]) : c.t === "date" ? fmtDate(r[c.k]) || "" : (r[c.k] || "")}
                        </td>
                      );
                    })}
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(r)} className="w-6 h-6 inline-grid place-items-center hover:bg-indigo-50 text-indigo-600 rounded" data-testid={`gider-edit-${r.id}`}><Edit size={12}/></button>
                      <button onClick={() => openCopy(r)} title="Kopyalayarak yeni kayıt" className="w-6 h-6 inline-grid place-items-center hover:bg-sky-50 text-sky-600 rounded" data-testid={`gider-copy-${r.id}`}><Copy size={12}/></button>
                      <button onClick={() => handleDelete(r)} className="w-6 h-6 inline-grid place-items-center hover:bg-rose-50 text-rose-600 rounded ml-1" data-testid={`gider-delete-${r.id}`}><Trash2 size={12}/></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => !saving && setModalOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="gider-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
              <div className="font-bold text-slate-900">{editing ? "Gider Avansı Düzenle" : copySource ? "Yeni Gider Avansı (Kopya)" : "Yeni Gider Avansı"}</div>
              <button onClick={() => setModalOpen(false)} className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={16}/></button>
            </div>
            {!editing && copySource && (
              <div className="mx-5 mt-4 flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-xs text-sky-900" data-testid="gider-copy-banner">
                <Copy size={14} className="text-sky-600 shrink-0" />
                <span className="flex-1"><strong>{copySource.base_no || copySource.borclu || "Seçili kayıt"}</strong> kaydından kopyalandı: müvekkil, borçlu, dosya numaraları, yatırıldığı yer ve mahkeme/icra dairesi dolduruldu; tutarlar ve tarihler boş.</span>
              </div>
            )}
            {!editing && draftInfo && (
              <div className="mx-5 mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-900" data-testid="gider-draft-banner">
                <AlertCircle size={14} className="text-amber-600 shrink-0" />
                <span className="flex-1">Yarım kalan taslak geri yüklendi · {new Date(draftInfo).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}</span>
                <button type="button" onClick={discardDraft} data-testid="gider-draft-discard"
                  className="font-semibold text-amber-900 hover:text-rose-700 underline underline-offset-2">Taslağı Sil</button>
              </div>
            )}
            {!editing && !draftInfo && !copySource && giderDraftHasContent(form) && (
              <div className="mx-5 mt-3 text-[11px] text-slate-500" data-testid="gider-draft-autosave">Taslak otomatik saklanıyor · pencere kapansa da kaybolmaz</div>
            )}
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              {EDITABLE_COLUMNS.map((c) => (
                <div key={c.k} className={c.t === "textarea" ? "md:col-span-2" : ""}>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1">
                    {c.l}
                  </label>
                  {c.t === "textarea" ? (
                    <textarea
                      value={form[c.k] ?? ""}
                      onChange={(e) => setForm((f) => ({ ...f, [c.k]: e.target.value }))}
                      rows={2}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                      data-testid={`gider-f-${c.k}`}
                    />
                  ) : c.k === "muvekkil" ? (
                    <select
                      value={form[c.k] ?? ""}
                      onChange={(e) => setForm((f) => ({ ...f, [c.k]: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                      data-testid={`gider-f-${c.k}`}
                    >
                      <option value="">— Müvekkil seçin —</option>
                      {creditors.map((cr) => <option key={cr.id} value={cr.ad}>{cr.ad}</option>)}
                    </select>
                  ) : c.t === "tpl" ? (
                    <TemplateInput kind={c.k} label={c.l} value={form[c.k]} testid={`gider-f-${c.k}`}
                      onChange={(v) => setForm((f) => ({ ...f, [c.k]: v }))} />
                  ) : c.t === "avans_turu_tpl" ? (
                    <div className="flex items-center gap-1">
                      <AvansTuruDot color={colorForAvansTuru(form[c.k])} size={10} className="ml-0.5" />
                      <select
                        value={form[c.k] ?? ""}
                        onChange={(e) => setForm((f) => ({ ...f, [c.k]: e.target.value }))}
                        className="flex-1 px-2 py-1.5 border border-slate-300 rounded text-sm"
                        data-testid={`gider-f-${c.k}`}
                      >
                        <option value="">— Şablondan seçin —</option>
                        {avansTuruOptions.filter(t => t.is_active).map((t) => (
                          <option key={t.id} value={t.ad}>{t.ad}</option>
                        ))}
                        {/* Preserve legacy value if it's not in template list anymore (edit case) */}
                        {form[c.k] && !avansTuruOptions.some(t => t.ad === form[c.k]) && (
                          <option value={form[c.k]}>{form[c.k]} (eski)</option>
                        )}
                      </select>
                      {canManageTemplates && (
                        <button
                          type="button"
                          onClick={() => setShowTemplateManager(true)}
                          className="w-7 h-7 grid place-items-center hover:bg-indigo-50 text-indigo-600 border border-slate-300 rounded"
                          title="Avans türü şablonlarını yönet"
                          data-testid="gider-f-avans_turu-manage"
                        ><Settings size={13}/></button>
                      )}
                    </div>
                  ) : c.t === "select" ? (
                    <select
                      value={form[c.k] ?? ""}
                      onChange={(e) => setForm((f) => ({ ...f, [c.k]: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                      data-testid={`gider-f-${c.k}`}
                    >
                      <option value="">— Seçin —</option>
                      {(c.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : c.t === "money" ? (
                    <input
                      type="number"
                      step="0.01"
                      value={form[c.k] ?? 0}
                      onChange={(e) => setForm((f) => ({ ...f, [c.k]: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                      data-testid={`gider-f-${c.k}`}
                    />
                  ) : c.t === "date" ? (
                    <DateInput value={form[c.k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [c.k]: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" data-testid={`gider-f-${c.k}`} />
                  ) : (
                    <input
                      type="text"
                      value={form[c.k] ?? ""}
                      onChange={(e) => setForm((f) => ({ ...f, [c.k]: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
                      data-testid={`gider-f-${c.k}`}
                    />
                  )}
                </div>
              ))}
              {/* Live Fark preview */}
              <div className="md:col-span-2 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 flex items-center justify-between" data-testid="gider-f-fark-preview">
                <span className="text-xs font-semibold text-emerald-900">Fark (otomatik: Yatırılan − İade Gelen)</span>
                <span className="font-bold text-emerald-900">{fmtMoney(previewFark)}</span>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button onClick={() => setModalOpen(false)} disabled={saving} className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-md">İptal</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-md" data-testid="gider-submit">
                {saving ? "Kaydediliyor..." : (editing ? "Güncelle" : "Kaydet")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit modal */}
      {showBulkEdit && (
        <GiderBulkEditModal
          selectedIds={Array.from(selectedIds)}
          creditors={creditors}
          avansTuruOptions={avansTuruOptions}
          onClose={() => setShowBulkEdit(false)}
          onSaved={() => {
            setShowBulkEdit(false);
            setSelectedIds(new Set());
            load();
          }}
        />
      )}

      {/* Bulk Fill Yatırıldığı Yer modal (one-tap for empty rows) */}
      {showBulkFillYer && (
        <GiderBulkFillYerModal
          emptyIds={rows.filter((r) => !r.yatirildigi_yer || !String(r.yatirildigi_yer).trim()).map((r) => r.id)}
          onClose={() => setShowBulkFillYer(false)}
          onSaved={() => {
            setShowBulkFillYer(false);
            load();
          }}
        />
      )}

      {/* Template manager */}
      {showTemplateManager && (
        <AvansTuruTemplateManager
          onClose={() => setShowTemplateManager(false)}
          onChanged={() => { loadAvansTuruTemplates(); load(); }}
        />
      )}
    </div>
  );
}


function GiderBulkEditModal({ selectedIds, creditors, avansTuruOptions, onClose, onSaved }) {
  const [muvekkil, setMuvekkil] = useState("");
  const [masrafTarihi, setMasrafTarihi] = useState("");
  const [iadeTarihi, setIadeTarihi] = useState("");
  const [avansTuru, setAvansTuru] = useState("");
  const [yatirildigiYer, setYatirildigiYer] = useState("");
  const [daire, setDaire] = useState("");
  const [notlar, setNotlar] = useState("");
  const [saving, setSaving] = useState(false);

  const touched =
    (muvekkil ? 1 : 0) + (masrafTarihi ? 1 : 0) + (iadeTarihi ? 1 : 0) +
    (avansTuru ? 1 : 0) + (yatirildigiYer ? 1 : 0) + (daire.trim() ? 1 : 0) + (notlar ? 1 : 0);

  const submit = async () => {
    if (touched === 0) {
      toast.error("Düzenlenecek en az bir alan seçin");
      return;
    }
    const patch = {};
    if (muvekkil) patch.muvekkil = muvekkil;
    if (masrafTarihi) patch.masraf_yatirma_tarihi = masrafTarihi;
    if (iadeTarihi) patch.iade_tarihi = iadeTarihi;
    if (avansTuru) patch.avans_turu = avansTuru;
    if (yatirildigiYer) patch.yatirildigi_yer = yatirildigiYer;
    if (daire.trim()) patch.mahkeme_icra_dairesi = daire.trim();
    if (notlar) patch.notlar = notlar;

    if (!window.confirm(`${selectedIds.length} gider avansı satırında ${touched} alan güncellenecek. Onaylıyor musunuz?`)) return;
    setSaving(true);
    try {
      const r = await api.post("/gider-avans/bulk-edit", { ids: selectedIds, patch });
      toast.success(`${r.data.updated} satır güncellendi`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu düzenleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="gider-bulk-edit-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Edit size={16} className="text-amber-600" />
            <h3 className="text-sm font-bold text-slate-900">Toplu Düzenle · {selectedIds.length} satır</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={14} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-900 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <div>Yalnızca <strong>doldurduğunuz</strong> alanlar güncellenecek. Boş bıraktıklarınız dokunulmaz.</div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">Müvekkil (değiştir)</label>
            <select
              value={muvekkil}
              onChange={(e) => setMuvekkil(e.target.value)}
              data-testid="gider-bulk-edit-muvekkil"
              className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm"
            >
              <option value="">— Değiştirme —</option>
              {creditors.map((c) => <option key={c.id} value={c.ad}>{c.ad}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">Avans Tarihi — Masraf Yatırma (değiştir)</label>
            <DateInput value={masrafTarihi} onChange={(e) => setMasrafTarihi(e.target.value)} data-testid="gider-bulk-edit-masraf-tarihi" className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">İade Tarihi (değiştir)</label>
            <DateInput value={iadeTarihi} onChange={(e) => setIadeTarihi(e.target.value)} data-testid="gider-bulk-edit-iade-tarihi" className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">Avans Türü (değiştir)</label>
            <select value={avansTuru} onChange={(e) => setAvansTuru(e.target.value)} data-testid="gider-bulk-edit-avans-turu" className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm">
              <option value="">— Değiştirme —</option>
              {(avansTuruOptions || []).filter(t => t.is_active).map((t) => (
                <option key={t.id} value={t.ad}>{t.ad}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">Yatırıldığı Yer (değiştir)</label>
            <select value={yatirildigiYer} onChange={(e) => setYatirildigiYer(e.target.value)} data-testid="gider-bulk-edit-yatirildigi-yer" className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm">
              <option value="">— Değiştirme —</option>
              <option value="Mahkeme">Mahkeme</option>
              <option value="İcra">İcra</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">Mahkeme / İcra Dairesi (değiştir)</label>
            <TemplateInput kind="mahkeme_icra_dairesi" label="Mahkeme / İcra Dairesi" value={daire} onChange={setDaire} testid="gider-bulk-edit-daire" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">Not (değiştir)</label>
            <input type="text" value={notlar} onChange={(e) => setNotlar(e.target.value)} data-testid="gider-bulk-edit-notlar" className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" placeholder="Boş bırakırsan dokunulmaz" />
          </div>
          {touched > 0 && (
            <div className="text-xs text-emerald-700 font-semibold">
              {touched} alan değişecek · {selectedIds.length} satırda uygulanacak
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold hover:bg-white">İptal</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || touched === 0}
            data-testid="gider-bulk-edit-save-btn"
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-lg text-sm font-semibold"
          >
            {saving ? "Güncelleniyor..." : `Güncelle (${selectedIds.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}



// ======================= GiderAvansTrendCard =======================
function GiderAvansTrendCard({ year, onYearChange, data, totals, loading }) {
  const nowYear = new Date().getFullYear();
  const years = [nowYear - 2, nowYear - 1, nowYear, nowYear + 1];
  const [open, setOpen] = useState(false);

  // Build a compact "cards summary" row — current year totals + biggest fark month
  const peakFark = useMemo(() => {
    if (!data || data.length === 0) return null;
    return data.reduce((best, d) => (d.fark > (best?.fark ?? -Infinity) ? d : best), null);
  }, [data]);

  // Chart tick formatter — short TL (K / M)
  const compact = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(0) + "K";
    return String(n);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const d = payload[0]?.payload || {};
    return (
      <div className="bg-white border border-slate-200 rounded-md shadow-lg px-3 py-2 text-xs">
        <div className="font-bold text-slate-900 mb-1">{label} {year}</div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-emerald-700 font-semibold">Yatırılan</span>
          <span className="font-mono">{fmtMoney(d.yatirilan)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sky-700 font-semibold">İade</span>
          <span className="font-mono">{fmtMoney(d.iade)}</span>
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-slate-200 mt-1 pt-1">
          <span className="text-slate-700 font-semibold">Fark</span>
          <span className={`font-mono font-bold ${d.fark >= 0 ? "text-rose-700" : "text-emerald-700"}`}>{fmtMoney(d.fark)}</span>
        </div>
        {d.delta_pct !== null && d.delta_pct !== undefined && (
          <div className="flex items-center justify-between gap-4 text-[10px]">
            <span className="text-slate-500">Yatırılan Δ (önceki aya göre)</span>
            <span className={`font-semibold inline-flex items-center gap-0.5 ${d.delta_pct >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
              {d.delta_pct >= 0 ? <TrendingUp size={10}/> : <TrendingDown size={10}/>}
              {d.delta_pct > 0 ? "+" : ""}{d.delta_pct}%
            </span>
          </div>
        )}
        <div className="text-[10px] text-slate-400 mt-0.5">{d.count || 0} kayıt</div>
      </div>
    );
  };

  return (
    <div className="mb-3 bg-white border border-slate-200 rounded-lg p-3" data-testid="gider-trend-card">
      <div className={`flex items-start justify-between gap-3 flex-wrap ${open ? "mb-2" : ""}`}>
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-left hover:opacity-80 transition-opacity" data-testid="gider-trend-toggle">
          <div className="inline-flex items-center gap-2 font-bold text-slate-900 text-sm">
            {open ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
            <TrendingUp size={14} className="text-emerald-700" /> Aylık Trend
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">({year})</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5" data-testid="gider-trend-totals">
            Toplam Yatırılan: <strong className="text-emerald-700">{fmtMoney(totals?.yatirilan)}</strong> · İade: <strong className="text-sky-700">{fmtMoney(totals?.iade)}</strong> · Fark: <strong className={(totals?.fark || 0) >= 0 ? "text-rose-700" : "text-emerald-700"}>{fmtMoney(totals?.fark)}</strong> · {totals?.count || 0} kayıt
            {peakFark && peakFark.fark > 0 && (
              <> · Zirve Fark: <strong>{peakFark.ay_label}</strong> ({fmtMoney(peakFark.fark)})</>
            )}
          </div>
        </button>
        <div className="flex items-center gap-1">
          {years.map((y) => (
            <button
              key={y}
              onClick={() => onYearChange(y)}
              className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${y === year ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"}`}
              data-testid={`gider-trend-year-${y}`}
            >
              {y}
            </button>
          ))}
        </div>
      </div>
      {open && <div style={{ width: "100%", height: 220 }} data-testid="gider-trend-chart">
        {loading ? (
          <div className="h-full grid place-items-center text-slate-400 text-sm">Yükleniyor...</div>
        ) : (
          <ResponsiveContainer>
            <ComposedChart data={data} margin={{ top: 24, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="ay_label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="yatirilan" name="Yatırılan" fill="#10B981" radius={[3, 3, 0, 0]} maxBarSize={28}>
                <LabelList
                  dataKey="delta_pct"
                  position="top"
                  formatter={(v) => (v === null || v === undefined) ? "" : `${v > 0 ? "+" : ""}${v}%`}
                  style={{ fontSize: 9, fontWeight: 600, fill: "#64748B" }}
                />
              </Bar>
              <Bar dataKey="iade" name="İade" fill="#0EA5E9" radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Line type="monotone" dataKey="fark" name="Fark" stroke="#E11D48" strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>}
    </div>
  );
}


/**
 * Toplu Eksik Doldur Modal
 * Lists IDs with empty 'yatirildigi_yer' and lets the user set one value for all.
 * Posts to /api/gider-avans/bulk-edit.
 */
function GiderBulkFillYerModal({ emptyIds, onClose, onSaved }) {
  const [value, setValue] = useState("Mahkeme");
  const [saving, setSaving] = useState(false);
  const total = emptyIds.length;

  const handleFill = async () => {
    if (!value) {
      toast.error("Bir değer seçin (Mahkeme / İcra)");
      return;
    }
    if (total === 0) {
      toast.info("Dolacak satır yok");
      return;
    }
    if (!window.confirm(`${total} satırda 'Yatırıldığı Yer' alanı "${value}" olarak güncellenecek. Devam?`)) return;
    setSaving(true);
    try {
      const r = await api.post("/gider-avans/bulk-edit", {
        ids: emptyIds,
        patch: { yatirildigi_yer: value },
      });
      toast.success(`${r.data?.updated ?? total} satır güncellendi`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="gider-bulk-fill-yer-modal">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
              <AlertCircle size={16} className="text-amber-600" /> Toplu Yatırıldığı Yer Doldur
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Yatırıldığı Yer alanı <strong>boş olan {total} satır</strong> güncellenecek.
            </p>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded hover:bg-slate-100 grid place-items-center"><X size={14}/></button>
        </div>
        <div className="p-4 space-y-4">
          {total === 0 ? (
            <div className="text-center py-8 text-emerald-700 text-sm font-semibold">
              Tüm satırlarda Yatırıldığı Yer dolu
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs text-slate-700 font-semibold mb-2 block">Hangi değeri uygulayalım?</label>
                <div className="grid grid-cols-2 gap-2">
                  {["Mahkeme", "İcra"].map((opt) => (
                    <button
                      key={opt}
                      data-testid={`bulk-fill-yer-${opt}`}
                      onClick={() => setValue(opt)}
                      className={`px-3 py-2 rounded-md text-sm font-semibold border transition-colors ${value === opt ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
                Bu işlem toplu uygulama yapar: tüm {total} satırın Yatırıldığı Yer alanı <strong>"{value}"</strong> olacak. İşlem geri alınamaz; gerekiyorsa önce yedek/filtre alın.
              </div>
            </>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-md">İptal</button>
            {total > 0 && (
              <button
                data-testid="bulk-fill-yer-apply"
                onClick={handleFill}
                disabled={saving}
                className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-md disabled:opacity-50"
              >
                {saving ? "Uygulanıyor..." : `${total} Satıra Uygula`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
